import React, { useState, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Collapse from '@mui/material/Collapse';
import LockIcon from '@mui/icons-material/Lock';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import { NumberChip } from '../Bookings/components/BookingIcons';
import Badge from '@mui/material/Badge';
import Fab from '@mui/material/Fab';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { useHeader } from '../../context/headerContext';
import HeaderSearch from './HeaderSearch';
import { useColorMode } from '../../context/colorModeContext';
import { getActiveFeatureFlags, getFeatureFlagsForPath } from '../../config/featureFlags';

// IMPORT HOOKS, COMPONENTS AND OTHER FUNCTIONS
import AuthContext, { useAuth } from '../../context/authContext';
import CountContext from '../../context/countContext';
import {
  FailedLoginWarningBanner,
  FailedLoginWarningDialog,
} from '../components/Login/FailedLoginWarningModal';
import { useEntertainment } from '../../context/entertainmentContext';
import LogoutTimer from '../components/Login/LogoutTimer';
import UserAvatar from '../components/Login/UserAvatar';
import { getPreloadedImage } from '../../util/imagePreloader';
import funzoneLogoWebp from '../../assets/logos/funzone.webp';
import funzoneWordmarkLightmode from '../../assets/logos/funzone_wordmark_lightmode.webp';
import funzoneWordmarkDarkmode from '../../assets/logos/funzone_wordmark_darkmode.webp';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import useFrontendUsage from '../../hooks/useFrontendUsage';
import { buildNewFrontendUrl } from '../../util/frontendMigration';
import { resolveViewTitle } from '../../util/viewTitle';

// Counters whose sidebar entry shows the animated warning stripes while the count
// is above zero. `openFoodOrders` (live food orders placed on site via QR code) is
// additionally non-deactivatable, so its stripes can never be suppressed.
const WARNING_MENU_STRIPE_COUNT_KEYS = new Set([
  'pendingBookings',
  'openFoodOrders',
  'escalatedChats',
  'memberspotTrainingDue',
  'memberspotTrainingSelf',
  'recruiteeOverdueApplicants',
  'customerExperienceFeedback',
  'copagoPriceDiscrepancies',
  'activeOutages',
  // A caller the phone assistant could not help is waiting for a call back — the same
  // "someone is waiting on us" case as an escalated chat.
  'agentCallbacks',
]);

/** Shared animated warning-stripes styling for sidebar menu buttons (attention items). */
const warningStripesListItemSx = theme => ({
  position: 'relative',
  overflow: 'hidden',
  color: 'common.white',
  boxShadow: `inset 0 0 0 1px ${alpha(
    theme.palette.common.white,
    theme.palette.mode === 'light' ? 0.35 : 0.18
  )}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: '-33.94px',
    backgroundImage: `repeating-linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.55)} 0 12px, ${alpha(
      theme.palette.warning.main,
      0.55
    )} 12px 24px)`,
    animation: 'pendingBookingsAttention 1.1s linear infinite',
    willChange: 'transform',
    pointerEvents: 'none',
    zIndex: 0,
  },
  '& > *': { position: 'relative', zIndex: 1 },
  '@keyframes pendingBookingsAttention': {
    from: { transform: 'translateX(0)' },
    to: { transform: 'translateX(-33.94px)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&::before': { animation: 'none' },
  },
  '&:hover::before': {
    backgroundImage: `repeating-linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.7)} 0 12px, ${alpha(
      theme.palette.warning.main,
      0.7
    )} 12px 24px)`,
  },
});

// `highlighted` marks the entry the sidebar search currently points at with the
// arrow keys — the one Enter opens. `onHighlight` lets the pointer take that
// selection over, so the highlight never sits somewhere else than the cursor.
const MenuItemWithCount = React.memo(({ item, onClose, highlighted = false, index, onHighlight }) => {
  const theme = useTheme();
  const countCtx = useContext(CountContext);
  const authCtx = useContext(AuthContext);
  const location = useLocation();
  const itemRef = useRef(null);
  // A counter the user has hidden (and still holds the right for) shows no badge
  // and no attention stripes, regardless of any value lingering in the context.
  const isDeactivated = authCtx.deactivatedCounterKeys?.has(item.countKey);
  const count = isDeactivated ? 0 : countCtx.counts[item.countKey] || 0;
  const isActive = location.pathname === item.link;
  const hasWarningStripes =
    count > 0 &&
    typeof item?.countKey === 'string' &&
    WARNING_MENU_STRIPE_COUNT_KEYS.has(item.countKey);

  const handleClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleMouseMove = useCallback(() => {
    if (onHighlight && !highlighted) onHighlight(index);
  }, [onHighlight, highlighted, index]);

  // Keep the keyboard selection visible while arrowing through a long hit list.
  useEffect(() => {
    if (highlighted && itemRef.current?.scrollIntoView) {
      itemRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted]);

  return (
    <ListItem disablePadding ref={itemRef} onMouseMove={handleMouseMove}>
      <ListItemButton
        component={item.isExternal ? 'a' : NavLink}
        to={item.link}
        target={item.newTab ? '_blank' : ''}
        onClick={handleClick}
        selected={isActive}
        sx={{
          ...(hasWarningStripes ? warningStripesListItemSx(theme) : {}),
          // The keyboard selection is a tinted row in the primary colour, not an
          // outline — an outlined box reads as a focus ring around an input and
          // floats above the neighbouring entries. `&&` raises the specificity so
          // the selection still shows on the entry of the current route, whose
          // `.Mui-selected` rule would otherwise win.
          ...(highlighted && {
            '&&': {
              backgroundColor: alpha(
                theme.palette.primary.main,
                theme.palette.mode === 'light' ? 0.1 : 0.16
              ),
              color: theme.palette.primary.main,
            },
          }),
          '&.Mui-selected': {
            backgroundColor: 'action.selected',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.selected',
            },
          },
        }}
      >
        <ListItemText primary={item.name} />
        {count > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NumberChip number={count} size='small' color='error.main' />
          </Box>
        )}
        {item.isExpanded && (
          <ListItemIcon sx={{ minWidth: 36, paddingLeft: 1.5 }}>
            <LockIcon fontSize='small' color={isActive ? 'inherit' : 'action'} />
          </ListItemIcon>
        )}
      </ListItemButton>
    </ListItem>
  );
});

// Build a nested tree (structure only) from menu items using groupPath like "A/B/C".
// Counts are intentionally NOT baked into the structure so the tree can be memoized
// independently of the frequently-changing notification counts. Each group stores the
// countKeys of all its descendants so live aggregated totals and warning-stripe state
// can be derived cheaply at render time without rebuilding the tree on every count tick.
function buildMenuTree(menuItems) {
  const root = { key: '', name: '', type: 'group', children: new Map() };
  for (const item of menuItems) {
    const path = (item.groupPath || '').trim();
    if (!path) {
      // Attach leaf directly to root
      const key = `__leaf__:${item.link}`;
      root.children.set(key, { key, type: 'item', item });
      continue;
    }
    const segments = path
      .split('/')
      .map(s => s.trim())
      .filter(Boolean);
    let node = root;
    let currentPath = '';
    for (const segment of segments) {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      if (!node.children.has(currentPath)) {
        node.children.set(currentPath, {
          key: currentPath,
          name: segment,
          type: 'group',
          children: new Map(),
        });
      }
      node = node.children.get(currentPath);
    }
    const leafKey = `__leaf__:${item.link}`;
    node.children.set(leafKey, { key: leafKey, type: 'item', item });
  }

  // Flatten groups with only one item - promote single items to parent level
  const flattenSingleItemGroups = node => {
    if (node.type === 'item') return node;

    const children = Array.from(node.children.values());
    const newChildren = new Map();

    for (const child of children) {
      if (child.type === 'group') {
        const flattenedChild = flattenSingleItemGroups(child);
        const childItems = Array.from(flattenedChild.children.values());

        // If group has exactly one item, promote it to parent level
        if (childItems.length === 1 && childItems[0].type === 'item') {
          const singleItem = childItems[0];
          newChildren.set(singleItem.key, singleItem);
        } else {
          newChildren.set(flattenedChild.key, flattenedChild);
        }
      } else {
        newChildren.set(child.key, child);
      }
    }

    node.children = newChildren;
    return node;
  };

  // Collect the countKeys of all descendant leaves on each group so that aggregated
  // totals and warning-stripe state can be computed live from the counts context.
  const collectCountKeys = node => {
    if (node.type === 'item') {
      return node.item?.countKey ? [node.item.countKey] : [];
    }
    const keys = [];
    for (const child of node.children.values()) {
      const childKeys = collectCountKeys(child);
      for (const k of childKeys) keys.push(k);
    }
    node.countKeys = keys;
    return keys;
  };

  const flattenedRoot = flattenSingleItemGroups(root);
  collectCountKeys(flattenedRoot);
  return flattenedRoot;
}

// Sum the live counts for a group's descendant countKeys. Counters the user has
// hidden are excluded so a collapsed group doesn't surface a total for badges that
// are individually suppressed.
function getGroupCountTotal(countKeys, counts, deactivatedKeys) {
  if (!countKeys || countKeys.length === 0) return 0;
  let sum = 0;
  for (const key of countKeys) {
    if (deactivatedKeys && deactivatedKeys.has(key)) continue;
    sum += counts[key] || 0;
  }
  return sum;
}

// Whether any of a group's descendant countKeys currently warrants warning stripes.
// Hidden counters never trigger the attention styling.
function groupHasWarningStripes(countKeys, counts, deactivatedKeys) {
  if (!countKeys || countKeys.length === 0) return false;
  for (const key of countKeys) {
    if (deactivatedKeys && deactivatedKeys.has(key)) continue;
    if ((counts[key] || 0) > 0 && WARNING_MENU_STRIPE_COUNT_KEYS.has(key)) return true;
  }
  return false;
}

// Collapsible group header. Reads counts from context so the aggregated chip and the
// warning-stripe styling update live, without re-rendering the surrounding tree. The
// aggregates are only relevant while the group is collapsed.
const MenuGroupButton = React.memo(function MenuGroupButton({ node, isOpen, onToggle }) {
  const theme = useTheme();
  const countCtx = useContext(CountContext);
  const authCtx = useContext(AuthContext);
  const deactivatedKeys = authCtx.deactivatedCounterKeys;
  const totalCount = isOpen ? 0 : getGroupCountTotal(node.countKeys, countCtx.counts, deactivatedKeys);
  const hasWarningStripes = isOpen
    ? false
    : groupHasWarningStripes(node.countKeys, countCtx.counts, deactivatedKeys);

  return (
    <ListItemButton
      onClick={onToggle}
      sx={{
        py: 0.5,
        ...(hasWarningStripes ? warningStripesListItemSx(theme) : {}),
      }}
    >
      {isOpen ? (
        <ListItemText
          primary={node.name}
          primaryTypographyProps={{
            variant: 'caption',
            sx: theme => ({
              fontWeight: 600,
              letterSpacing: 0.3,
              color:
                theme.palette.mode === 'light'
                  ? theme.palette.grey[800]
                  : theme.palette.grey[300],
            }),
          }}
        />
      ) : (
        <ListItemText
          primary={node.name}
          primaryTypographyProps={{
            variant: 'body1',
            color: 'inherit',
            sx: { fontWeight: 400 },
          }}
        />
      )}
      {!isOpen && totalCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
          <NumberChip number={totalCount} size='small' color='error.main' />
        </Box>
      )}
      {isOpen ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
    </ListItemButton>
  );
});

// A single collapsible group plus its (lazily mounted) children. The count-dependent
// header lives in MenuGroupButton so live count updates do not re-render the nested
// subtree rendered here.
function MenuGroup({ node, expandedGroups, onToggleGroup, onCloseItem }) {
  const isOpen = expandedGroups[node.key] ?? true;
  const handleToggle = useCallback(() => onToggleGroup(node.key), [onToggleGroup, node.key]);

  return (
    <Box>
      <ListItem disablePadding>
        <MenuGroupButton node={node} isOpen={isOpen} onToggle={handleToggle} />
      </ListItem>
      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <List component='div' disablePadding sx={{ pl: 1 }}>
          <MenuTree
            node={node}
            expandedGroups={expandedGroups}
            onToggleGroup={onToggleGroup}
            onCloseItem={onCloseItem}
          />
        </List>
      </Collapse>
    </Box>
  );
}

// Renders the children of a tree node. Deliberately does not consume the counts context
// so that frequent count updates only re-render the small count-aware leaves/headers
// instead of the whole tree.
function MenuTree({ node, expandedGroups, onToggleGroup, onCloseItem }) {
  if (!node || !node.children) return null;
  const entries = Array.from(node.children.values());
  return (
    <>
      {entries.map(child =>
        child.type === 'item' ? (
          <MenuItemWithCount key={child.key} item={child.item} onClose={onCloseItem} />
        ) : (
          <MenuGroup
            key={child.key}
            node={child}
            expandedGroups={expandedGroups}
            onToggleGroup={onToggleGroup}
            onCloseItem={onCloseItem}
          />
        )
      )}
    </>
  );
}

function FailedLoginWarningHandler() {
  const auth = useAuth();
  const warning = auth?.failedLoginWarning;

  if (!warning) return null;

  const allSelf = warning.attempts?.every(a => a.level === 'self');
  const hasNearbyOrForeign = warning.attempts?.some(
    a => a.level === 'nearby' || a.level === 'foreign'
  );

  if (allSelf) {
    return (
      <FailedLoginWarningBanner warning={warning} onDismiss={auth.clearFailedLoginWarning} />
    );
  }

  if (hasNearbyOrForeign) {
    return (
      <FailedLoginWarningDialog
        open={true}
        onClose={auth.clearFailedLoginWarning}
        warning={warning}
        userName={auth.currentUser?.userName}
        getAuthHeaders={auth.getAuthHeaders}
      />
    );
  }

  return null;
}

// Slim notice for the page currently open, shown only when that page owes its
// existence — or a part of it — to a feature flag that would be off on live.
// Every other view stays free of it, and on live it never appears at all, since
// no flag resolves to enabled there while `enabled` is false. The complete list
// of active flags sits in the environment chip's tooltip in the header.
function FeatureFlagBanner() {
  const location = useLocation();
  const { pageFlags, partialFlags } = useMemo(
    () => getFeatureFlagsForPath(location.pathname),
    [location.pathname]
  );

  const relevantFlags = [...pageFlags, ...partialFlags];
  if (relevantFlags.length === 0) return null;

  const labelList = flags => flags.map(flag => flag.label).join(', ');
  const sentences = [];
  if (pageFlags.length > 0) {
    sentences.push(`Diese Seite ist nur über Feature-Flag sichtbar: ${labelList(pageFlags)}`);
  }
  if (partialFlags.length > 0) {
    sentences.push(
      `Teile dieser Seite sind nur über Feature-Flag sichtbar: ${labelList(partialFlags)}`
    );
  }

  return (
    <Tooltip
      title={
        <Box component='span' sx={{ whiteSpace: 'pre-line' }}>
          {relevantFlags.map(flag => `${flag.label}: ${flag.description}`).join('\n\n')}
        </Box>
      }
    >
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'warning.light',
          color: 'common.black',
          fontSize: '0.75rem',
          fontWeight: 500,
          lineHeight: 1.5,
          textAlign: 'center',
          px: 2,
          py: 0.25,
          cursor: 'help',
        }}
      >
        {sentences.join(' · ')}
      </Box>
    </Tooltip>
  );
}

function ButtonAppBar(props) {
  const authCtx = useContext(AuthContext);
  const countCtx = useContext(CountContext);
  const { currentLogo } = useEntertainment();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const headerSearchInputRef = useRef(null);
  const menuFilterInputRef = useRef(null);
  const [hasOpenCounts, setHasOpenCounts] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const navigate = useNavigate();
  const { customActions } = useHeader();
  // FunZone itself is shown with the brand wordmark (transparent artwork, so the ink
  // follows the color mode); every other entertainment keeps its own tile logo.
  const headerLogoSrc = useMemo(() => {
    if (currentLogo !== funzoneLogoWebp) {
      return currentLogo;
    }
    return theme.palette.mode === 'light' ? funzoneWordmarkLightmode : funzoneWordmarkDarkmode;
  }, [currentLogo, theme.palette.mode]);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [menuFilter, setMenuFilter] = useState('');
  const [colorModeMenuAnchor, setColorModeMenuAnchor] = useState(null);
  const { colorModePreferenceForDisplay, setColorModePreference } = useColorMode();
  const { trackSwitchToNewFrontend } = useFrontendUsage();
  const showMenuFilter = authCtx?.preferences?.showSidebarMenuFilter !== false;
  const ctrlFRedirectTarget = authCtx?.preferences?.ctrlFRedirectTarget || 'off';
  // The box searches bookings and vouchers, so either read right is enough.
  // `booking:read:assigned` belongs to a different domain and would only buy an
  // empty box, so it no longer counts.
  const hasHeaderSearchRights = useMemo(() => {
    return (
      authCtx.checkRightAny('internalBooking:read:own') ||
      authCtx.checkRightAny('voucher:read:own')
    );
  }, [authCtx]);

  const filteredMenuItems = useMemo(() => {
    if (!showMenuFilter || !menuFilter.trim()) {
      return authCtx.menuItems;
    }
    const filterLower = menuFilter.toLowerCase();
    return authCtx.menuItems.filter(item => {
      const nameMatch = item.name.toLowerCase().includes(filterLower);
      const groupPathMatch = item.groupPath?.toLowerCase().includes(filterLower);
      const termsMatch =
        Array.isArray(item.menuSearchTerms) &&
        item.menuSearchTerms.some(term => term.toLowerCase().includes(filterLower));
      return nameMatch || groupPathMatch || termsMatch;
    });
  }, [authCtx.menuItems, menuFilter, showMenuFilter]);

  // Structural menu tree. Rebuilt only when the (filtered) menu items change — never on
  // count updates — so frequent notification-count changes neither rebuild the tree nor
  // re-render the whole sidebar. Group totals are derived live from the counts context.
  const menuTree = useMemo(() => buildMenuTree(filteredMenuItems), [filteredMenuItems]);

  const isFilterActive = useMemo(() => {
    return showMenuFilter && menuFilter.trim().length > 0;
  }, [showMenuFilter, menuFilter]);

  // Index of the filter hit the arrow keys point at. A changed query starts over
  // at the top hit, so Enter always opens what the list shows first.
  const [filterHighlight, setFilterHighlight] = useState(0);
  useEffect(() => {
    setFilterHighlight(0);
  }, [menuFilter]);

  const filterHitCount = isFilterActive ? filteredMenuItems.length : 0;
  const highlightedFilterIndex = filterHitCount ? Math.min(filterHighlight, filterHitCount - 1) : -1;

  // Check if logo is already preloaded globally
  useEffect(() => {
    setLogoLoaded(false);
    const preloadedLogo = getPreloadedImage(headerLogoSrc);
    if (preloadedLogo) {
      setLogoLoaded(true);
    } else {
      const img = new Image();
      img.onload = () => setLogoLoaded(true);
      img.onerror = () => setLogoLoaded(true);
      img.src = headerLogoSrc;
    }
  }, [headerLogoSrc]);

  // Check for any open counts
  useEffect(() => {
    const checkOpenCounts = async () => {
      try {
        // Get all count values from the counts object, excluding interval IDs
        const countValues = Object.entries(countCtx.counts)
          .filter(([key]) => !key.endsWith('_interval'))
          .map(([_, value]) => value);

        const hasOpen = countValues.some(count => count > 0);
        setHasOpenCounts(hasOpen);
      } catch (error) {
        console.error('Error checking counts:', error);
        toast.error('Fehler beim Laden der Anzahl der offenen Anfragen');
        setHasOpenCounts(false);
      }
    };
    checkOpenCounts();
  }, [countCtx.counts]);

  // sidebar is open by default on mobile, closed on desktop
  const [drawerOpen, setDrawerOpen] = useState(() => !isMobile);

  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (!authCtx?.isLoggedIn) return;
    if (ctrlFRedirectTarget === 'off') return;

    const onKeyDown = e => {
      const key = String(e?.key || '').toLowerCase();
      if (key !== 'f') return;
      if (!(e.ctrlKey || e.metaKey)) return;

      if (e.shiftKey) {
        const canUseCtrlShiftF = showMenuFilter && ctrlFRedirectTarget !== 'off';

        if (canUseCtrlShiftF) {
          e.preventDefault();
          if (!drawerOpen) setDrawerOpen(true);
          window.requestAnimationFrame(() => {
            const el = menuFilterInputRef.current;
            if (el && typeof el.focus === 'function') el.focus();
            if (el && typeof el.select === 'function') el.select();
          });
          return;
        }
      }

      if (ctrlFRedirectTarget === 'sidebarMenu') {
        if (!showMenuFilter) return;
        e.preventDefault();
        if (!drawerOpen) setDrawerOpen(true);
        window.requestAnimationFrame(() => {
          const el = menuFilterInputRef.current;
          if (el && typeof el.focus === 'function') el.focus();
          if (el && typeof el.select === 'function') el.select();
        });
        return;
      }

      if (ctrlFRedirectTarget === 'headerSearch') {
        if (!hasHeaderSearchRights) return;
        e.preventDefault();
        setIsSearchExpanded(true);
        window.requestAnimationFrame(() => {
          const el = headerSearchInputRef.current;
          if (el && typeof el.focus === 'function') el.focus();
          if (el && typeof el.select === 'function') el.select();
        });
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [
    authCtx?.isLoggedIn,
    ctrlFRedirectTarget,
    drawerOpen,
    showMenuFilter,
    hasHeaderSearchRights,
  ]);

  const location = useLocation();

  // Switches to the new management frontend, recording the switch (incl. the
  // current page and view) for the migration statistics first.
  const handleSwitchToNewFrontend = useCallback(async () => {
    const path = location.pathname;
    const viewTitle = resolveViewTitle({ pathname: path, menuItems: authCtx.menuItems });
    try {
      await trackSwitchToNewFrontend({
        path,
        viewTitle: viewTitle || undefined,
        origin: 'header-button',
      });
    } catch (_e) {
      // Tracking must never block the switch.
    }
    window.location.assign(buildNewFrontendUrl(path));
  }, [location.pathname, authCtx.menuItems, trackSwitchToNewFrontend]);

  const handleDrawerToggle = useCallback(() => {
    setDrawerOpen(!drawerOpen);
  }, [drawerOpen]);

  const handleMenuItemClose = useCallback(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  const openFilteredMenuItem = useCallback(
    idx => {
      const item = filteredMenuItems?.[idx];
      if (!item) return;

      handleMenuItemClose();

      if (item.isExternal) {
        const target = item.newTab ? '_blank' : '_self';
        window.open(item.link, target);
        return;
      }

      navigate(item.link);
    },
    [filteredMenuItems, handleMenuItemClose, navigate]
  );

  const handleToggleGroup = useCallback(key => {
    setExpandedGroups(prev => ({ ...prev, [key]: !(prev[key] ?? true) }));
  }, []);

  const handleCollapseAllGroups = useCallback(() => {
    setExpandedGroups(prev => {
      const next = { ...prev };
      const stack = [menuTree];
      while (stack.length) {
        const n = stack.pop();
        if (!n || !n.children) continue;
        for (const child of n.children.values()) {
          if (child.type === 'group') {
            next[child.key] = false;
            stack.push(child);
          }
        }
      }
      return next;
    });
  }, [menuTree]);

  const handleExpandAllGroups = useCallback(() => {
    setExpandedGroups(prev => {
      const next = { ...prev };
      const stack = [menuTree];
      while (stack.length) {
        const n = stack.pop();
        if (!n || !n.children) continue;
        for (const child of n.children.values()) {
          if (child.type === 'group') {
            next[child.key] = true;
            stack.push(child);
          }
        }
      }
      return next;
    });
  }, [menuTree]);

  const areAllGroupsCollapsed = useMemo(() => {
    const stack = [menuTree];
    while (stack.length) {
      const n = stack.pop();
      if (!n || !n.children) continue;
      for (const child of n.children.values()) {
        if (child.type === 'group') {
          if (expandedGroups[child.key] !== false) {
            return false;
          }
          stack.push(child);
        }
      }
    }
    return true;
  }, [menuTree, expandedGroups]);

  // A route change collapses the box again on narrow screens.
  useEffect(() => {
    setIsSearchExpanded(false);
  }, [location.pathname]);

  const drawerWidth = 240;

  const isStaging = import.meta.env.VITE_IS_STAGING === 'true';
  const isDev = import.meta.env.VITE_IS_DEV === 'true' || Boolean(import.meta.env.DEV);
  const isProdBuild = Boolean(import.meta.env.PROD);
  const isLive = isProdBuild && !isStaging && !isDev;

  // Complete flag list for the environment chip's tooltip — the per-page banner
  // only names the flags belonging to the view currently open.
  const activeFeatureFlags = useMemo(() => getActiveFeatureFlags(), []);

  const showLiveAdminWarning = useMemo(() => {
    if (!isLive) return false;
    if (!authCtx?.isLoggedIn) return false;

    const roles = authCtx?.currentUser?.roles;
    if (!Array.isArray(roles) || roles.length === 0) return false;
    return roles.some(r => String(r || '').trim().toLowerCase() === 'superadmin');
  }, [isLive, authCtx?.isLoggedIn, authCtx?.currentUser?.roles]);

  const hasStripedHeaderBackground = showLiveAdminWarning;

  const drawer = useMemo(
    () => (
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        anchor='left'
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          ...(!isMobile && {
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: !drawerOpen ? 'translateX(-100%)' : 'translateX(0)',
            position: 'fixed',
            height: '100%',
          }),
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            height: '100vh',
            boxSizing: 'border-box',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1200,
            backgroundImage: 'none',
            ...(!isMobile && {
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: !drawerOpen ? 'translateX(-100%)' : 'translateX(0)',
              position: 'fixed',
              height: '100%',
            }),
          },
        }}
      >
        <Toolbar />
        {!isFilterActive && (
          <Box sx={{ position: 'absolute', bottom: 0, right: 0, zIndex: 1200, height: 40 }}>
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: theme => theme.zIndex.drawer + 1,
              }}
            >
              <Tooltip title={areAllGroupsCollapsed ? 'Alle ausklappen' : 'Alle einklappen'}>
                <IconButton
                  size='small'
                  onClick={areAllGroupsCollapsed ? handleExpandAllGroups : handleCollapseAllGroups}
                  sx={{
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    '&:hover': {
                      bgcolor: 'action.selected',
                    },
                  }}
                >
                  {areAllGroupsCollapsed ? (
                    <OpenInFullIcon fontSize='small' />
                  ) : (
                    <CloseFullscreenIcon fontSize='small' />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        <Box sx={{ overflow: 'auto' }}>
          {showMenuFilter && (
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                backgroundColor: 'background.paper',
                p: 1,
                pb: 1,
              }}
            >
              <TextField
                size='small'
                placeholder='Suche Menüpunkte'
                value={menuFilter}
                onChange={e => setMenuFilter(e.target.value)}
                inputRef={menuFilterInputRef}
                onKeyDown={e => {
                  // Arrow keys walk the hits (wrapping at both ends), Enter opens
                  // the highlighted one — the search box keeps the focus.
                  if (highlightedFilterIndex < 0) return;

                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setFilterHighlight((highlightedFilterIndex + 1) % filterHitCount);
                    return;
                  }

                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setFilterHighlight(
                      (highlightedFilterIndex - 1 + filterHitCount) % filterHitCount
                    );
                    return;
                  }

                  if (e.key === 'Enter') {
                    e.preventDefault();
                    openFilteredMenuItem(highlightedFilterIndex);
                  }
                }}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  ),
                  endAdornment: menuFilter && (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        onClick={() => setMenuFilter('')}
                        sx={{ color: 'text.secondary' }}
                        edge='end'
                      >
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={theme => ({
                  '& .MuiOutlinedInput-root': {
                    backgroundColor:
                      theme.palette.mode === 'light'
                        ? theme.palette.grey[50]
                        : alpha(theme.palette.common.white, 0.05),
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.text.secondary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary,
                    '&::placeholder': {
                      color: theme.palette.text.secondary,
                      opacity: 1,
                    },
                  },
                  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                    color: theme.palette.text.secondary,
                  },
                })}
              />
            </Box>
          )}
          <List>
            {/* On mobile the logo (which links to /welcome) is hidden, so the
                drawer is the only way back to the dashboard. Show a Dashboard
                entry on xs only. */}
            <ListItem
              key='dashboard-home'
              disablePadding
              sx={{ display: { xs: 'block', sm: 'none' } }}
            >
              <ListItemButton
                onClick={() => {
                  navigate('/welcome');
                  handleMenuItemClose();
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <SpaceDashboardIcon />
                </ListItemIcon>
                <ListItemText primary='Dashboard' />
              </ListItemButton>
            </ListItem>
            {isFilterActive ? (
              // Render flat list without grouping when filter is active
              filteredMenuItems.map((item, idx) => (
                <MenuItemWithCount
                  key={item.link}
                  item={item}
                  onClose={handleMenuItemClose}
                  index={idx}
                  highlighted={idx === highlightedFilterIndex}
                  onHighlight={setFilterHighlight}
                />
              ))
            ) : (
              // Render normal tree structure when no filter is active
              <MenuTree
                node={menuTree}
                expandedGroups={expandedGroups}
                onToggleGroup={handleToggleGroup}
                onCloseItem={handleMenuItemClose}
              />
            )}
            {/* Show different logout options based on device type */}
            {authCtx.isCounterDevice ? (
              // Counter device: show "Sperren" and "Schicht beenden"
              <>
                <ListItem key={'lock'} disablePadding>
                  <ListItemButton onClick={() => authCtx.logout()}>
                    <ListItemText
                      primaryTypographyProps={{ color: 'warning.main' }}
                      primary={'Sperren'}
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem key={'endShift'} disablePadding>
                  <ListItemButton onClick={() => authCtx.endShiftHandler()}>
                    <ListItemText
                      primaryTypographyProps={{ color: 'error' }}
                      primary={'Schicht beenden'}
                    />
                  </ListItemButton>
                </ListItem>
              </>
            ) : (
              // Private/Company device: show normal "Logout"
              <ListItem key={'logout'} disablePadding>
                <ListItemButton onClick={() => authCtx.logout()}>
                  <ListItemText primaryTypographyProps={{ color: 'error' }} primary={'Logout'} />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    ),
    [
      isMobile,
      drawerOpen,
      handleDrawerToggle,
      handleMenuItemClose,
      navigate,
      filteredMenuItems,
      menuTree,
      expandedGroups,
      handleToggleGroup,
      authCtx.logout,
      handleCollapseAllGroups,
      showMenuFilter,
      isFilterActive,
      // The filter's own state: without these the memoized drawer keeps a stale
      // render (and a stale key handler), so the arrow-key selection never moves.
      // `menuFilter` also drives the controlled input, which previously only
      // followed along because a keystroke happened to produce a new
      // `filteredMenuItems` array.
      menuFilter,
      filterHitCount,
      highlightedFilterIndex,
      openFilteredMenuItem,
      areAllGroupsCollapsed,
      handleExpandAllGroups,
      theme.palette.mode,
    ]
  );

  // Ensure groups default opened/closed based on user preference.
  // If the preference value changes, force-reset all group states to the new default.
  const prevDefaultExpandedRef = useRef(
    authCtx?.preferences?.sidebarGroupsExpandedByDefault !== false
  );
  useEffect(() => {
    const defaultExpanded = authCtx?.preferences?.sidebarGroupsExpandedByDefault !== false;
    const forceReset = prevDefaultExpandedRef.current !== defaultExpanded;
    prevDefaultExpandedRef.current = defaultExpanded;

    // Only seed defaults for groups that don't have an explicit state yet (or reset all
    // when the preference flipped). Bail out without a state update when nothing changed
    // so this does not trigger an avoidable sidebar re-render.
    setExpandedGroups(prev => {
      const next = forceReset ? {} : { ...prev };
      let changed = forceReset;
      const stack = [menuTree];
      while (stack.length) {
        const n = stack.pop();
        if (!n || !n.children) continue;
        for (const child of n.children.values()) {
          if (child.type === 'group') {
            if (forceReset || next[child.key] === undefined) {
              next[child.key] = defaultExpanded;
              changed = true;
            }
            stack.push(child);
          }
        }
      }
      return changed ? next : prev;
    });
  }, [menuTree, authCtx?.preferences?.sidebarGroupsExpandedByDefault]);

  // If user is not logged in, render only the children without header
  if (!authCtx.isLoggedIn) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          ...viewRootTextPrimarySx,
        }}
      >
        {props.children}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        color='grey'
        position='fixed'
        sx={{
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            ...(isStaging ? { backgroundColor: 'red' } : undefined),
            ...(showLiveAdminWarning
              ? {
                  position: 'relative',
                  overflow: 'hidden',
                  color: 'common.black',
                  backgroundColor: 'rgba(255, 235, 59, 0.92)',
                  backgroundImage:
                    'repeating-linear-gradient(135deg, rgba(211,47,47,0.55) 0 12px, rgba(255,235,59,0.70) 12px 24px)',
                }
              : undefined),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge
              color='error'
              variant='dot'
              invisible={!hasOpenCounts || drawerOpen}
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                '& .MuiBadge-dot': {
                  top: 12,
                  right: 12,
                },
              }}
            >
              <IconButton
                color='inherit'
                aria-label='open drawer'
                edge='start'
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </Badge>

            <Box
              sx={{
                alignItems: 'center',
                flexShrink: 0,
                opacity: {
                  xs: isSearchExpanded ? 0 : 1,
                  sm: isSearchExpanded ? 0 : 1,
                  md: 1,
                },
                visibility: {
                  xs: isSearchExpanded ? 'hidden' : 'visible',
                  sm: isSearchExpanded ? 'hidden' : 'visible',
                  md: 'visible',
                },
                display: { xs: 'none', sm: 'flex' },
                transition: 'all 0.3s ease-in-out',
                cursor: hasHeaderSearchRights ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (hasHeaderSearchRights) {
                  navigate('/welcome');
                }
              }}
            >
              <Box
                component='img'
                src={headerLogoSrc}
                sx={{
                  height: 35,
                  opacity: logoLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                }}
                onLoad={() => setLogoLoaded(true)}
              />
            </Box>
          </Box>

          {/* Search field in the center: bookings and vouchers, never taken
              over by a view. */}
          {hasHeaderSearchRights && (
            <HeaderSearch
              inputRef={headerSearchInputRef}
              isSearchExpanded={isSearchExpanded}
              setIsSearchExpanded={setIsSearchExpanded}
              hasStripedHeaderBackground={hasStripedHeaderBackground}
            />
          )}

          {/* Right side elements */}
          <Box
            sx={{
              alignItems: 'center',
              flexShrink: 0,
              opacity: { xs: isSearchExpanded ? 0 : 1, sm: isSearchExpanded ? 0 : 1, md: 1 },
              visibility: {
                xs: isSearchExpanded ? 'hidden' : 'visible',
                sm: isSearchExpanded ? 'hidden' : 'visible',
                md: 'visible',
              },
              display: { xs: 'none', sm: 'flex' },
              transition: 'all 0.3s ease-in-out',
              gap: 1,
            }}
          >
            {!authCtx.isCounterDevice && (
              <>
                <Tooltip title='Zum neuen Management-Frontend wechseln'>
                  <IconButton
                    color='inherit'
                    aria-label='Zum neuen Management-Frontend wechseln'
                    onClick={handleSwitchToNewFrontend}
                    size='small'
                  >
                    <AutoAwesomeIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Farbschema'>
                  <IconButton
                    color='inherit'
                    aria-label='Farbschema'
                    aria-controls={colorModeMenuAnchor ? 'color-mode-menu' : undefined}
                    aria-haspopup='true'
                    aria-expanded={colorModeMenuAnchor ? 'true' : undefined}
                    onClick={e => setColorModeMenuAnchor(e.currentTarget)}
                    size='small'
                  >
                    {colorModePreferenceForDisplay === 'light' ? (
                      <LightModeIcon />
                    ) : colorModePreferenceForDisplay === 'dark' ? (
                      <DarkModeIcon />
                    ) : (
                      <BrightnessAutoIcon />
                    )}
                  </IconButton>
                </Tooltip>
                <Menu
                  id='color-mode-menu'
                  anchorEl={colorModeMenuAnchor}
                  open={Boolean(colorModeMenuAnchor)}
                  onClose={() => setColorModeMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    selected={colorModePreferenceForDisplay === 'light'}
                    onClick={async () => {
                      setColorModeMenuAnchor(null);
                      try {
                        await setColorModePreference('light');
                      } catch (_e) {
                        toast.error('Speichern der Einstellungen fehlgeschlagen');
                      }
                    }}
                  >
                    Hell
                  </MenuItem>
                  <MenuItem
                    selected={colorModePreferenceForDisplay === 'dark'}
                    onClick={async () => {
                      setColorModeMenuAnchor(null);
                      try {
                        await setColorModePreference('dark');
                      } catch (_e) {
                        toast.error('Speichern der Einstellungen fehlgeschlagen');
                      }
                    }}
                  >
                    Dunkel
                  </MenuItem>
                  <MenuItem
                    selected={colorModePreferenceForDisplay === 'system'}
                    onClick={async () => {
                      setColorModeMenuAnchor(null);
                      try {
                        await setColorModePreference('system');
                      } catch (_e) {
                        toast.error('Speichern der Einstellungen fehlgeschlagen');
                      }
                    }}
                  >
                    System
                  </MenuItem>
                </Menu>
              </>
            )}
            {customActions}
            {authCtx.isCounterDevice && <LogoutTimer />}
            {authCtx.isCounterDevice && <UserAvatar />}
            {(isStaging || isDev) && (
              <Tooltip
                title={
                  <Box component='span' sx={{ whiteSpace: 'pre-line' }}>
                    {activeFeatureFlags.length === 0
                      ? 'Keine Feature-Flags aktiv.'
                      : `Feature-Flags aktiv (hier sichtbar, live ausgeblendet):\n\n${activeFeatureFlags
                          .map(flag => `${flag.label}: ${flag.description}`)
                          .join('\n\n')}`}
                  </Box>
                }
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    ml: 1,
                    border: 2,
                    borderColor: isStaging ? 'black' : 'success.main',
                    color: isStaging ? 'black' : 'success.main',
                    backgroundColor: isStaging
                      ? 'rgba(211, 47, 47, 0.2)'
                      : 'rgba(76, 175, 80, 0.2)',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    cursor: 'help',
                  }}
                >
                  {isStaging ? 'Staging' : 'Dev'}
                </Box>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer toggle button positioned below header */}
      {isMobile && (
        <Fab
          size='small'
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            left: drawerOpen ? drawerWidth : -15,
            top: '93px',
            zIndex: theme.zIndex.drawer - 1,
            transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backgroundColor: theme.palette.mode === 'light' ? 'grey.200' : 'rgba(33, 33, 33, 0.9)',
            color: theme.palette.mode === 'light' ? 'text.primary' : 'grey.300',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'light' ? 'grey.300' : 'rgba(33, 33, 33, 1)',
            },
            boxShadow: 3,
            border: !drawerOpen
              ? theme.palette.mode === 'light'
                ? '1px solid rgba(0, 0, 0, 0.12)'
                : '1px solid rgba(255, 255, 255, 0.67)'
              : 'none',
            borderRadius: '0 8px 8px 0',
            cursor: 'pointer',
            minWidth: 35,
            height: 48,
          }}
        >
          <Badge
            color='error'
            variant='dot'
            invisible={!hasOpenCounts || drawerOpen}
            sx={{
              '& .MuiBadge-dot': {
                top: 4,
                right: 3,
              },
            }}
          >
            <DragIndicatorIcon fontSize='small' />
          </Badge>
        </Fab>
      )}

      {drawer}
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          px: 3,
          pb: 3,
          pt: 0,
          width: `calc(100% - ${drawerWidth}px)`,
          ml: drawerOpen && !isMobile ? `${drawerWidth}px` : 0,
          transition: 'margin-left 0.3s ease',
          height: '100vh',
          overflow: 'auto',
          ...viewRootTextPrimarySx,
        }}
      >
        <Toolbar />
        <FeatureFlagBanner />
        <FailedLoginWarningHandler />
        {props.children}
      </Box>
    </Box>
  );
}

export default ButtonAppBar;
